import { useEffect, useRef, useState, type ReactNode } from 'react';

import { Modal, Notice } from '@/components/Modal';
import { PersonCardModal } from '@/components/PersonCard';
import { useSession } from '@/lib/auth/session';
import { ApprovalDocumentView, type DocView } from '@/pages/approval/ApprovalDocumentView';
import { displayFields } from '@/pages/approval/formSchema';
import { bandKind, referenceLabels, signableLines } from '@/pages/approval/lineOrder';
import { paperDate } from '@/pages/approval/paperForm';
import { fetchLinePreview, type LinePreview } from '@/pages/approval/useApproval';
import {
  fetchPreviewPdf,
  issuanceFileUrl,
  useIssuanceTemplates,
  useMyIssuances,
  useRequestIssuance,
  type Issuance,
  type IssuanceTemplate,
} from '@/pages/mypage/useIssuance';
import { safeHref } from '@/lib/utils/url';

/**
 * 증명서 발급 탭 (docs/18_2026-07-31_multi-ai-agent-design.md §10).
 *
 * 흐름: **① 서식 고르기 → ② 용도·제출처 적기 → ③ 미리보기로 확인하고 신청**.
 * 성명·소속·재직기간은 입력란이 없다 — 인사 표에서만 오기 때문이다(§10.1). 사용자가 채우는 값은
 * 용도와 제출처뿐이고, 그래서 위조할 여지가 없다.
 *
 * <h2>왜 번호를 붙였나</h2>
 * 서식이 3종에서 15종으로 늘면서 이 화면이 "고를 것과 적을 것과 누를 것" 이 뒤섞인 한 덩어리가 됐다.
 * 무엇을 입력해야 하는지가 라벨에만 적혀 있으면, 처음 온 사람은 세로로 늘어선 칸을 전부 읽어야 한다.
 * 세 걸음으로 잘라 번호를 붙이면 "지금 내가 어디에 있는가" 가 눈으로 보인다.
 *
 * <h2>미리보기는 화면 한가운데 모달로 연다</h2>
 * 예전에는 폼 아래에 iframe 을 이어 붙였다. 그러면 미리보기를 누른 사람이 문서를 보려고 스크롤을
 * 내려야 하고(누른 자리에서는 아무 일도 안 일어난 것처럼 보인다), 탭 전체가 화면 두세 배 길이로 자란다.
 * 문서는 **읽으라고 만든 것**이라 화면 가운데 크게 띄우고, 닫으면 폼이 그대로 남는다.
 *
 * <p>미리보기에는 **"미승인" 워터마크가 박혀 있고 직인이 없다.** 서버가 렌더할 때 합성하므로 지울 수
 * 없다 — 결재를 거치지 않은 종이가 밖으로 나가는 것을 막는다(§10.5).
 */
export function CertificateTab() {
  const { data: templates, isPending, error } = useIssuanceTemplates();
  const { data: history } = useMyIssuances();
  const requestIssuance = useRequestIssuance();

  const [templateKey, setTemplateKey] = useState<string>('');
  const [purpose, setPurpose] = useState('');
  const [recipient, setRecipient] = useState('');
  const [copies, setCopies] = useState(1);
  const [submitted, setSubmitted] = useState<Issuance | null>(null);

  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  /** 함께 보는 기안서 — 결재를 타는 서식만. 상신하면 서버가 같은 서식·같은 규정으로 결재선을 세운다. */
  const [draftView, setDraftView] = useState<DocView | null>(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const { data: session } = useSession();
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  // blob URL 은 명시적으로 풀지 않으면 탭이 살아 있는 동안 PDF 가 메모리에 남는다.
  // 새 미리보기를 만들 때와 화면을 떠날 때 모두 풀어야 한다.
  const previewUrlRef = useRef<string | null>(null);
  useEffect(() => {
    previewUrlRef.current = previewUrl;
  }, [previewUrl]);
  useEffect(() => () => {
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
  }, []);

  // 첫 서식을 미리 골라 둔다 — 들어오자마자 미리보기를 누를 수 있게.
  // noUncheckedIndexedAccess 가 켜져 있어 [0] 은 undefined 일 수 있다. 옵셔널 체이닝으로 받는다.
  useEffect(() => {
    const first = templates?.[0]?.templateKey;
    if (!templateKey && first) setTemplateKey(first);
  }, [templates, templateKey]);

  const selected: IssuanceTemplate | undefined = templates?.find((t) => t.templateKey === templateKey);

  /*
    용도는 **결재를 타는 서식에서만** 필수다. 결재자가 승인/반려를 가르는 근거가 그 한 줄이라,
    비워 두면 결재함에서 "무엇에 쓰는 문서인지 모를 문서" 가 된다. 즉시 발급되는 서식(자격증
    보유현황 등)은 본인 자료를 옮겨 적을 뿐이고 문서에 용도가 찍히지도 않으므로 막지 않는다.
  */
  const purposeRequired = Boolean(selected?.requiresApproval);
  const purposeMissing = purposeRequired && !purpose.trim();

  const makePreview = async () => {
    setBusy(true);
    setFailure(null);
    try {
      const formKey = selected?.requiresApproval ? selected.approvalFormKey : null;
      const [url, line] = await Promise.all([
        fetchPreviewPdf({
          templateKey,
          purpose: purpose.trim() || undefined,
          recipient: recipient.trim() || undefined,
        }),
        formKey ? fetchLinePreview(formKey, null) : Promise.resolve(null),
      ]);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(url);
      setDraftView(
        formKey && line && selected
          ? toDraftView({
              formKey,
              templateName: selected.name,
              templateKey,
              drafterName: session?.user?.name ?? '-',
              drafterRank: session?.user?.rank ?? null,
              purpose: purpose.trim(),
              recipient: recipient.trim(),
              line,
            })
          : null,
      );
      setViewerOpen(true);
    } catch (e) {
      setFailure(e instanceof Error ? e.message : '미리보기를 만들지 못했습니다.');
    } finally {
      setBusy(false);
    }
  };

  // 신청은 미리보기와 같은 입력으로 보낸다 — 눈으로 확인한 그 문서가 그대로 신청되어야 한다.
  const submit = async () => {
    setFailure(null);
    setSubmitted(null);
    try {
      const created = await requestIssuance.mutateAsync({
        templateKey,
        purpose: purpose.trim() || undefined,
        recipient: recipient.trim() || undefined,
        copies,
      });
      setSubmitted(created);
      setViewerOpen(false);
    } catch (e) {
      setFailure(e instanceof Error ? e.message : '신청하지 못했습니다.');
    }
  };

  if (isPending) {
    return (
      <div className="flex justify-center py-12">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }
  if (error) return <Notice tone="error">서식 목록을 불러오지 못했습니다.</Notice>;
  if (!templates?.length) {
    return <Notice tone="info">발급 가능한 서식이 없습니다. 인사팀이 서식을 등록하면 여기에 나타납니다.</Notice>;
  }

  const submitLabel = requestIssuance.isPending
    ? '보내는 중…'
    : selected?.requiresApproval
      ? '결재 상신'
      : '즉시 발급';

  return (
    /*
      폼의 폭을 묶는다. 카드 전체 폭을 쓰면 넓은 화면에서 라벨과 입력칸이 손가락 두 뼘씩 떨어져
      한 줄을 눈으로 잇기 어려워진다. 실제로 적는 값은 짧은 문자열 둘과 숫자 하나뿐이다.
    */
    <div className="space-y-4" style={{ maxWidth: '720px' }}>
      {/* ── ① 서식 ──────────────────────────────────────────────────────── */}
      <Step no={1} title="서식 고르기" hint="어떤 증명서를 뗄지 고릅니다.">
        <div className="flex items-center gap-2 flex-wrap">
          <select
            id="cert-template"
            aria-label="증명서 서식"
            className="input-field"
            style={{ maxWidth: '320px', fontSize: '13px', padding: '6px 10px' }}
            value={templateKey}
            onChange={(e) => {
              setTemplateKey(e.target.value);
              setSubmitted(null);
              setFailure(null);
            }}
          >
            {templates.map((t) => (
              <option key={t.templateKey} value={t.templateKey}>{t.name}</option>
            ))}
          </select>
          {/*
            결재 여부는 서식마다 다르고, 그 차이가 "오늘 받는가 / 며칠 걸리는가" 를 가른다.
            버튼 글자(즉시 발급 · 결재 상신)에만 있으면 다 적고 난 뒤에야 알게 된다.
          */}
          {selected && (
            <span className={selected.requiresApproval ? 'badge badge-yellow' : 'badge badge-green'}>
              {selected.requiresApproval ? '인사팀 결재 후 발급' : '즉시 발급'}
            </span>
          )}
        </div>
        {selected?.description && <p style={{ ...mutedText, marginTop: '6px' }}>{selected.description}</p>}
      </Step>

      {/* ── ② 사람이 채우는 값은 이 둘(과 부수)뿐이다 ────────────────────── */}
      <Step
        no={2}
        title="용도·제출처 적기"
        hint="성명·소속·직위·재직기간은 인사 정보에서 자동으로 채워집니다. 값이 잘못돼 있으면 인사팀에 문의하세요."
      >
        {/* 좁은 화면에서는 한 줄에 하나씩 — 세 칸을 나란히 두면 '용도' 가 두 글자 폭이 된다. */}
        <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_88px]">
          <LabeledInput
            id="cert-purpose"
            label="용도"
            required={purposeRequired}
            placeholder="예: 전세대출"
            value={purpose}
            onChange={setPurpose}
          />
          <LabeledInput
            id="cert-recipient"
            label="제출처"
            placeholder="예: 국민은행"
            value={recipient}
            onChange={setRecipient}
          />
          <div>
            <FieldLabel htmlFor="cert-copies">부수</FieldLabel>
            <input
              id="cert-copies"
              type="number"
              min={1}
              max={20}
              className="input-field"
              style={compactInput}
              value={copies}
              onChange={(e) => setCopies(Math.max(1, Math.min(20, Number(e.target.value) || 1)))}
            />
          </div>
        </div>
        {purposeMissing && <p style={hintError}>{/* 결재를 거치는 서식은 용도를 적어야 합니다. */}</p>}
      </Step>

      {/* ── ③ 확인하고 신청 ─────────────────────────────────────────────── */}
      <Step
        no={3}
        title="확인하고 신청"
        hint="미리보기에는 “미승인” 워터마크가 찍히고 직인이 없습니다. 정식 증명서는 발급이 끝난 뒤 문서번호와 직인이 찍혀 나옵니다."
      >
        <div className="flex items-center gap-2 flex-wrap">
          <button type="button" className="btn-secondary" onClick={makePreview} disabled={busy || !templateKey}>
            {busy ? '만드는 중…' : '미리보기'}
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={submit}
            disabled={requestIssuance.isPending || !templateKey || purposeMissing}
          >
            {submitLabel}
          </button>
          {previewUrl && !viewerOpen && (
            <button type="button" className="btn-ghost" onClick={() => setViewerOpen(true)}>
              미리보기 다시 열기
            </button>
          )}
        </div>
      </Step>

      {failure && <Notice tone="error">{failure}</Notice>}

      {submitted && (
        <Notice tone={submitted.status === 'ISSUED' ? 'success' : 'info'}>
          {submitted.status === 'ISSUED' ? (
            <>
              발급이 완료되었습니다. 문서번호 <strong>{submitted.docNo}</strong>
              {!submitted.sealed && ' (직인 미등록 상태로 발급됨)'} — 아래 이력에서 다운로드하세요.
            </>
          ) : (
            <>{/* 인사팀에 결재를 상신했습니다. 승인되면 문서번호와 직인이 찍힌 문서가 발급됩니다. */}</>
          )}
        </Notice>
      )}

      <IssuanceHistory items={history ?? []} />

      {viewerOpen && previewUrl && (
        <PreviewViewer
          title={selected?.name ?? '증명서'}
          url={previewUrl}
          draftView={draftView}
          submitLabel={submitLabel}
          submitDisabled={requestIssuance.isPending || purposeMissing}
          onSubmit={submit}
          onClose={() => setViewerOpen(false)}
        />
      )}
    </div>
  );
}

/**
 * 미리보기 뷰어 — <b>화면 한가운데</b>.
 *
 * 폼 아래에 이어 붙이지 않는 이유는 두 가지다. 문서가 접힌 화면 밖에서 열려 누른 사람이 결과를 못 보고,
 * 탭 자체가 화면 몇 배 길이로 자라 이력 표까지 밀려난다. 모달은 열린 자리가 곧 시선이 있는 자리다.
 *
 * <p>높이를 vh 로 잡되 모달 껍데기(머리말·꼬리말·안쪽 여백 ≈ 190px)를 빼야 문서가 잘리지 않는다.
 * 낮은 화면에서도 종이 한 장은 보이도록 바닥을 320px 로 둔다.
 */
function PreviewViewer({
  title,
  url,
  draftView,
  submitLabel,
  submitDisabled,
  onSubmit,
  onClose,
}: {
  title: string;
  url: string;
  /** 있으면 기안서를 왼쪽에, 증명서를 오른쪽에 나란히 띄운다. */
  draftView: DocView | null;
  submitLabel: string;
  submitDisabled: boolean;
  onSubmit: () => void;
  onClose: () => void;
}) {
  const paneHeight = 'max(320px, calc(90vh - 190px))';
  const certFrame = (
    <iframe
      title="증명서 미리보기"
      src={safeHref(url)}
      className="w-full rounded"
      style={{ height: paneHeight, border: '1px solid var(--border)', display: 'block' }}
    />
  );
  return (
    <Modal
      title={`미리보기 · ${title}`}
      onClose={onClose}
      maxWidth={draftView ? '1600px' : '900px'}
      footer={
        <>
          {/* 새 탭에서 열면 브라우저 PDF 뷰어의 인쇄 버튼을 그대로 쓸 수 있다.
              서버가 대신 인쇄하지 않는 이유는 §10.8 — 누가 실물을 뽑았는지 남아야 한다. */}
          <a className="btn-secondary" href={safeHref(url)} target="_blank" rel="noreferrer">
            새 탭에서 열기 · 인쇄
          </a>
          <button type="button" className="btn-primary" onClick={onSubmit} disabled={submitDisabled}>
            이대로 {submitLabel}
          </button>
        </>
      }
    >
      {draftView ? (
        <div className="grid gap-3 lg:grid-cols-2">
          <div className="min-w-0 space-y-1">
            <p style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)' }}>기안서</p>
            <div className="rounded" style={{ height: paneHeight, overflow: 'auto', border: '1px solid var(--border)' }}>
              <ApprovalDocumentView view={draftView} domId="cert-draft-preview" />
            </div>
          </div>
          <div className="min-w-0 space-y-1">
            <p style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)' }}>증명서</p>
            {certFrame}
          </div>
        </div>
      ) : (
        certFrame
      )}
    </Modal>
  );
}

/**
 * 상신하면 올라갈 기안서를 그대로 그린다. 제목·본문·formData 는 서버(IssuanceService.submitApproval)가
 * 만드는 모양과 같고, 결재선은 같은 서식의 규정 미리보기다. 서식명은 결재 창과 같은 자리 값(기안문)이다.
 */
function toDraftView({
  formKey,
  templateName,
  templateKey,
  drafterName,
  drafterRank,
  purpose,
  recipient,
  line,
}: {
  formKey: string;
  templateName: string;
  templateKey: string;
  drafterName: string;
  drafterRank: string | null;
  purpose: string;
  recipient: string;
  line: LinePreview;
}): DocView {
  const formData = { templateKey, subjectName: drafterName, purpose };
  return {
    formName: '기안문',
    title: `[${templateName}] ${drafterName}`,
    docNo: null,
    dateLabel: paperDate(new Date()),
    drafterName,
    drafterOrg: null,
    drafterRank,
    urgency: 'NORMAL',
    boxes: [
      { kind: 'DRAFT' as const, position: '기안', name: drafterName, signature: null },
      ...signableLines(line.lines).map((l) => ({
        kind: bandKind(l.lineType),
        position: l.isFinal ? `${l.approverPosition ?? ''} 전결`.trim() : l.approverPosition,
        name: l.approverName,
        signature: null,
        final: l.isFinal,
      })),
    ],
    references: referenceLabels(line.lines),
    fields: displayFields(formKey, formData),
    longs: [],
    body: `용도: ${purpose || '-'} / 제출처: ${recipient || '-'}`,
  };
}

/** 걸음 하나. 번호·제목·설명을 묶어 두면 화면마다 다른 모양으로 흩어지지 않는다. */
function Step({
  no,
  title,
  hint,
  children,
}: {
  no: number;
  title: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <section className="rounded-lg" style={{ border: '1px solid var(--border)', padding: '10px 12px' }}>
      <div className="flex items-center gap-2">
        <span
          aria-hidden
          className="flex items-center justify-center flex-shrink-0"
          style={{
            width: '18px',
            height: '18px',
            borderRadius: '999px',
            background: 'var(--accent)',
            color: 'white',
            fontSize: '11px',
            fontWeight: 700,
          }}
        >
          {no}
        </span>
        <h3 style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)' }}>{title}</h3>
      </div>
      {hint && <p style={{ ...mutedText, marginTop: '3px' }}>{hint}</p>}
      <div style={{ marginTop: '8px' }}>{children}</div>
    </section>
  );
}

function FieldLabel({ htmlFor, children, required }: { htmlFor: string; children: ReactNode; required?: boolean }) {
  return (
    <label
      htmlFor={htmlFor}
      style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '4px' }}
    >
      {children}
      {required && <span style={{ color: 'var(--danger)' }}> *</span>}
    </label>
  );
}

function LabeledInput({
  id,
  label,
  value,
  onChange,
  placeholder,
  required,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (next: string) => void;
  placeholder: string;
  required?: boolean;
}) {
  return (
    <div className="min-w-0">
      <FieldLabel htmlFor={id} required={required}>{label}</FieldLabel>
      <input
        id={id}
        className="input-field"
        style={compactInput}
        placeholder={placeholder}
        maxLength={100}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

/** 신청 이력. 결재 진행 상황과 발급된 문서 내려받기가 한 자리에 있다. */
function IssuanceHistory({ items }: { items: Issuance[] }) {
  // 담당자(전결자)를 누르면 조직도·인력배치와 같은 직원 카드를 띄운다.
  const [cardPersonId, setCardPersonId] = useState<string | null>(null);
  if (!items.length) return null;

  return (
    <div className="space-y-1">
      <p style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)' }}>신청 이력</p>
      <div className="overflow-x-auto">
        <table className="w-full" style={{ borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border)', color: 'var(--text-muted)', fontSize: '12px' }}>
              <th className="text-left py-1.5 px-2 font-medium">서식</th>
              <th className="text-left py-1.5 px-2 font-medium">용도</th>
              <th className="text-left py-1.5 px-2 font-medium">상태</th>
              <th className="text-left py-1.5 px-2 font-medium">문서번호</th>
              <th className="text-left py-1.5 px-2 font-medium">신청일</th>
              <th className="text-left py-1.5 px-2 font-medium">담당자</th>
              <th className="py-1.5 px-2" />
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} style={{ borderBottom: '1px solid var(--border)' }}>
                <td className="py-1.5 px-2">{it.templateName}</td>
                <td className="py-1.5 px-2">{it.purpose || '-'}</td>
                <td className="py-1.5 px-2"><StatusBadge issuance={it} /></td>
                <td className="py-1.5 px-2">{it.docNo || '-'}</td>
                <td className="py-1.5 px-2">{new Date(it.createdAt).toLocaleDateString('ko-KR')}</td>
                <td className="py-1.5 px-2">
                  {it.finalApproverPersonnelId ? (
                    <button
                      type="button"
                      onClick={() => setCardPersonId(it.finalApproverPersonnelId ?? null)}
                      style={{ color: 'var(--accent)', background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: '13px' }}
                    >
                      {it.finalApproverName ?? '-'}
                    </button>
                  ) : (
                    it.finalApproverName || '-'
                  )}
                </td>
                <td className="py-1.5 px-2 text-right">
                  {it.status === 'ISSUED' && (
                    // 발급본은 서버가 스냅샷에서 재현해 내려준다 — blob 을 들고 있을 필요가 없다.
                    <a className="badge badge-blue" href={issuanceFileUrl(it.id)} target="_blank" rel="noreferrer">
                      열기 · 인쇄
                    </a>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {cardPersonId && <PersonCardModal personnelId={cardPersonId} onClose={() => setCardPersonId(null)} />}
    </div>
  );
}

function StatusBadge({ issuance }: { issuance: Issuance }) {
  const label: Record<Issuance['status'], string> = {
    DRAFT: '반려됨',
    PENDING_APPROVAL: '결재 대기',
    ISSUED: issuance.sealed ? '발급 완료' : '발급(직인 없음)',
    REVOKED: '회수됨',
  };
  const tone: Record<Issuance['status'], string> = {
    DRAFT: 'var(--text-muted)',
    PENDING_APPROVAL: 'var(--accent)',
    ISSUED: issuance.sealed ? 'var(--success, #16a34a)' : 'var(--warning, #d97706)',
    REVOKED: 'var(--danger, #dc2626)',
  };
  return <span style={{ color: tone[issuance.status], fontWeight: 600 }}>{label[issuance.status]}</span>;
}

const compactInput = { fontSize: '13px', padding: '6px 10px' } as const;
const mutedText = { fontSize: '12px', color: 'var(--text-muted)', lineHeight: 1.6 } as const;
const hintError = { fontSize: '12px', color: 'var(--danger)', marginTop: '6px' } as const;
